import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { GiConfirmed } from 'react-icons/gi';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useLanguageActions from '../../../hooks/APIHooks/users/useLanguageActions';
import useProfileActions from '../../../hooks/APIHooks/users/useProfileActions';
import { SpokenLanguage } from '../../../types/languages';
import { User } from '../../../types/users';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const UpdateSpecializedLanguagesModal = ({
  languages,
  handleModalClose,
}: {
  languages: Array<SpokenLanguage> | undefined;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [selectedLanguages, setSelectedLanguages] = useState<
    Array<SpokenLanguage>
  >(languages || []);
  const [resetSelectField, setResetSelectField] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { getAddedLanguagesHandler } = useLanguageActions('addedLanguages');
  const { createSpecializedLangsHandler, updateSpecializedLangsHandler } =
    useProfileActions();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const addedLanguageOptions = useMemo(() => {
    if (!getAddedLanguagesHandler.data?.is_success) return [];

    return getAddedLanguagesHandler.data.data.filter(
      (lang) =>
        !selectedLanguages.some((selectedLang) => selectedLang.id === lang.id)
    );
  }, [getAddedLanguagesHandler.data, selectedLanguages]);

  // handler function to update specialized languages of profile page declared here
  const updateSpecializedLanguagesHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      if (!languages?.length) {
        const body = selectedLanguages.map((lang) => ({
          code: lang.code,
          name: lang.name,
        }));

        await createSpecializedLangsHandler
          .mutateAsync({ userId: currentUser?.id as number, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Languages Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: [
                  'user-profile-by-id',
                  (currentUser as User).user_profile_id,
                ],
              });
            } else {
              toast.error(res.message || 'Languages Update Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        const body = {
          updated_by: currentUser?.username as string,
          languages_spoken: selectedLanguages.map((lang) => lang.id),
        };

        await updateSpecializedLangsHandler
          .mutateAsync({ userId: currentUser?.id as number, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Languages Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: [
                  'user-profile-by-id',
                  (currentUser as User).user_profile_id,
                ],
              });
            } else {
              toast.error(res.message || 'Languages Update Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to handle removing selected languages from the list declared here
  const removeSelectedLangHandler = (langId: number) => {
    setSelectedLanguages((state) => state.filter((lang) => lang.id !== langId));
  };

  // rendering update specialized languages modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='specialized languages' />

      <Formik
        initialValues={{ languages: selectedLanguages }}
        onSubmit={updateSpecializedLanguagesHandler}
      >
        {(props) => (
          <Form>
            <div className='mt-5 space-y-5'>
              <AcademiaFormSelect
                options={addedLanguageOptions}
                name='language'
                label='Select Language'
                defaultValue={SELECT_FIELD_DEFAULT_VALUE}
                setValueOnChange={(value) => {
                  setSelectedLanguages((state) => [
                    ...state,
                    {
                      id: value.id,
                      name: value.name,
                      code: value.code as string,
                    },
                  ]);

                  setResetSelectField(true);
                }}
                resetSelectField={resetSelectField}
                setResetSelectField={() => setResetSelectField(false)}
                showSearchInput
                isRequired
              />

              {selectedLanguages?.length ? (
                <div className='flex flex-wrap items-center gap-3 border-2 border-dashed border-legal-dark-blue rounded-lg p-3'>
                  {selectedLanguages.map((language) => (
                    <SkillBadge
                      key={language.id}
                      badgeLabel={language.name}
                      color='#4CB9E7'
                      onClickRemove={() =>
                        removeSelectedLangHandler(language.id)
                      }
                      isEditing
                    />
                  ))}
                </div>
              ) : null}

              <AcademiaButton
                title={
                  selectedLanguages.length === 0
                    ? 'Please, add some languages first!'
                    : ''
                }
                type='submit'
                variant='premium'
                icon={<GiConfirmed />}
                isLoading={props.isSubmitting}
                isDisabled={selectedLanguages.length === 0}
                fullWidth
              >
                Confirm
              </AcademiaButton>
            </div>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default UpdateSpecializedLanguagesModal;
